import * as PIXI from "pixi.js";
import { ControlConstants } from "../../game/control/constant/ControlConstants";
/**
 * GraphicsUtil.ts
 */
export class GraphicsUtil {
    static centreSprite(displayObject, size) {
        if (size) {
            displayObject.width = size.x;
            displayObject.height = size.y;
        }
        displayObject.anchor.set(0.5, 0.5);
        displayObject.x = displayObject.width / 2;
        displayObject.y = displayObject.height / 2;
        return displayObject;
    }
    static setPivot(view, horizontalAlign = ControlConstants.CENTER, verticalAlign = ControlConstants.CENTER, realignToNewPivot = false) {
        let width;
        let height;
        switch (horizontalAlign) {
            case ControlConstants.LEFT:
                width = 0;
                break;
            default:
            case ControlConstants.CENTER:
                width = view.width / (2 * view.scale.x);
                break;
            case ControlConstants.RIGHT:
                width = view.width / view.scale.x;
                break;
        }
        switch (verticalAlign) {
            case ControlConstants.TOP:
                height = 0;
                break;
            default:
            case ControlConstants.CENTER:
                height = view.height / (2 * view.scale.y);
                break;
            case ControlConstants.BOTTOM:
                height = view.height / view.scale.y;
                break;
        }
        if (realignToNewPivot) {
            view.x += width;
            view.y += height;
        }
        view.pivot = new PIXI.Point(width, height);
    }
    static createRectangle(size, color = 0x00ff00, alpha = 0) {
        let rectangle = new PIXI.Graphics();
        rectangle.beginFill(color, alpha);
        rectangle.drawRect(0, 0, size.x, size.y);
        rectangle.endFill();
        return rectangle;
    }
    static createMask(size) {
        let rectangle = new PIXI.Graphics();
        rectangle.beginFill(0x00ff00, 1);
        rectangle.drawRect(0, 0, size.x, size.y);
        rectangle.endFill();
        return rectangle;
    }
    static createRoundedRectangle(size, radius, color = 0x00ff00, alpha = 0) {
        let rectangle = new PIXI.Graphics();
        rectangle.beginFill(color, alpha);
        rectangle.drawRoundedRect(0, 0, size.x, size.y, radius);
        rectangle.endFill();
        return rectangle;
    }
    static isInBounds(displayObject, point) {
        let displayBounds = displayObject.getBounds();
        let bounds = new PIXI.Rectangle(displayObject.worldTransform.tx, displayObject.worldTransform.ty, displayBounds.width, displayBounds.height);
        return bounds.contains(point.x, point.y);
    }
    static createBoundingBox(displayObject) {
        let graphics = new PIXI.Graphics();
        let bounds = displayObject.getBounds();
        let localBounds = displayObject.getLocalBounds();
        let width = bounds.width;
        let height = bounds.height;
        graphics.beginFill(0x22ff22, 0.25);
        graphics.lineStyle(1, 0xff0000, 0.6);
        graphics.drawRect(displayObject.x + localBounds.x, displayObject.y + localBounds.y, width, height);
        graphics.endFill();
        displayObject.parent.addChild(graphics);
    }
    static scaleToFit(container, width) {
        container.scale.set(1.0);
        while (container.width > width) {
            container.scale.set(container.scale.x * 0.96);
        }
    }
    static toRadians(degrees) {
        return (degrees * Math.PI) / 180;
    }
    static toDegrees(radians) {
        return (radians * 180) / Math.PI;
    }
    static clonePoint(point) {
        return new PIXI.Point(point.x, point.y);
    }
    /**
     * Converts the keys x and y to component+X and component+Y
     * @param component - {@link PixiPluginVarsComponents}
     * @returns Object with two keys: component+X and component+Y
     */
    static getPointComponents(component, point) {
        const out = {};
        out[`${component}X`] = point.x;
        out[`${component}Y`] = point.y;
        return out;
    }
    static intersectLineRect(a1, a2, r) {
        let ret = [];
        const intersectLeft = GraphicsUtil.intersectLines(a1, a2, new PIXI.Point(r.left, r.top), new PIXI.Point(r.left, r.bottom));
        const intersectTop = GraphicsUtil.intersectLines(a1, a2, new PIXI.Point(r.left, r.top), new PIXI.Point(r.right, r.top));
        const intersectRight = GraphicsUtil.intersectLines(a1, a2, new PIXI.Point(r.right, r.top), new PIXI.Point(r.right, r.bottom));
        const intersectBottom = GraphicsUtil.intersectLines(a1, a2, new PIXI.Point(r.left, r.bottom), new PIXI.Point(r.right, r.bottom));
        if (intersectLeft) {
            ret.push(intersectLeft);
        }
        if (intersectTop) {
            ret.push(intersectTop);
        }
        if (intersectRight) {
            ret.push(intersectRight);
        }
        if (intersectBottom) {
            ret.push(intersectBottom);
        }
        return ret;
    }
    /**
     * Find the point of intersection between two lines
     * @param a1 - Start point of line A
     * @param a2 - End point of line A
     * @param b1 - Start point of line B
     * @param b2 - Start point of line B
     * @returns The point of intersection of A and B, or undefined if lines do not intersect
     */
    static intersectLines(a1, a2, b1, b2) {
        const dX = a2.x - a1.x;
        const dY = a2.y - a1.y;
        const determinant = dX * (b2.y - b1.y) - (b2.x - b1.x) * dY;
        if (determinant === 0)
            return undefined; // parallel lines
        const lambda = ((b2.y - b1.y) * (b2.x - a1.x) + (b1.x - b2.x) * (b2.y - a1.y)) /
            determinant;
        const gamma = ((a1.y - a2.y) * (b2.x - a1.x) + dX * (b2.y - a1.y)) / determinant;
        // check if there is an intersection
        if (!(0 <= lambda && lambda <= 1) || !(0 <= gamma && gamma <= 1))
            return undefined;
        return new PIXI.Point(a1.x + lambda * dX, a1.y + lambda * dY);
    }
    /**
     * @returns The distance between two points
     */
    static pointDistance(p1, p2) {
        return Math.hypot(p1.x - p2.x, p1.y - p2.y);
    }
    /**
     * Returns the angle between two points
     */
    static pointRotation(pSource, pDest) {
        return Math.atan2(pDest.y - pSource.y, pDest.x - pSource.x);
    }
    /**
     * Rotates a line around a point on the line
     * @param line - The line segment expressed as two `PIXI.Point` objects.
     * @param rotation - Amount to rotate in radians.
     * @param progress - The progress along the line as a ratio
     */
    static rotateLine(line, rotation, progress = 0.5) {
        const matrix = new PIXI.Matrix(Math.cos(rotation), -Math.sin(rotation), Math.sin(rotation), Math.cos(rotation), 0, 0);
        const rotationPoint = this.lerpLine(line, progress);
        const translatedLine = [
            new PIXI.Point(line[0].x - rotationPoint.x, line[0].y - rotationPoint.y),
            new PIXI.Point(line[1].x - rotationPoint.x, line[1].y - rotationPoint.y),
        ];
        const rotatedTranslatedLine = [
            matrix.apply(translatedLine[0], new PIXI.Point()),
            matrix.apply(translatedLine[1], new PIXI.Point()),
        ];
        rotatedTranslatedLine[0].x += rotationPoint.x;
        rotatedTranslatedLine[1].x += rotationPoint.x;
        rotatedTranslatedLine[0].y += rotationPoint.y;
        rotatedTranslatedLine[1].y += rotationPoint.y;
        return rotatedTranslatedLine;
    }
    /**
     * Gives a linear interpolation or offset along a line segment
     * @param line - The line segment expressed as two PIXI.Point objects.
     * @param progress - The progress along the line as a ratio.
     * @param offset - Set `true` to return the answer as an offset from the origin point.
     */
    static lerpLine(line, progress, offset = false) {
        const offsetOut = new PIXI.Point((line[1].x - line[0].x) * progress, (line[1].y - line[0].y) * progress);
        if (offset) {
            return offsetOut;
        }
        return new PIXI.Point(line[0].x + offsetOut.x, line[0].y + offsetOut.y);
    }
    static colourNameToNumber(colour) {
        if (typeof colour === "number") {
            return colour;
        }
        if (typeof colour === "string" &&
            colour[0] === "#" &&
            colour.length === 7) {
            try {
                return Number("0x" + colour.substring(1));
            }
            catch (e) {
                return 0;
            }
        }
        else {
            var colours = {
                aliceblue: 0xf0f8ff,
                antiquewhite: 0xfaebd7,
                aqua: 0x00ffff,
                aquamarine: 0x7fffd4,
                azure: 0xf0ffff,
                beige: 0xf5f5dc,
                bisque: 0xffe4c4,
                black: 0x000000,
                blanchedalmond: 0xffebcd,
                blue: 0x0000ff,
                blueviolet: 0x8a2be2,
                brown: 0xa52a2a,
                burlywood: 0xdeb887,
                cadetblue: 0x5f9ea0,
                chartreuse: 0x7fff00,
                chocolate: 0xd2691e,
                coral: 0xff7f50,
                cornflowerblue: 0x6495ed,
                cornsilk: 0xfff8dc,
                crimson: 0xdc143c,
                cyan: 0x00ffff,
                darkblue: 0x00008b,
                darkcyan: 0x008b8b,
                darkgoldenrod: 0xb8860b,
                darkgray: 0xa9a9a9,
                darkgreen: 0x006400,
                darkkhaki: 0xbdb76b,
                darkmagenta: 0x8b008b,
                darkolivegreen: 0x556b2f,
                darkorange: 0xff8c00,
                darkorchid: 0x9932cc,
                darkred: 0x8b0000,
                darksalmon: 0xe9967a,
                darkseagreen: 0x8fbc8f,
                darkslateblue: 0x483d8b,
                darkslategray: 0x2f4f4f,
                darkturquoise: 0x00ced1,
                darkviolet: 0x9400d3,
                deeppink: 0xff1493,
                deepskyblue: 0x00bfff,
                dimgray: 0x696969,
                dodgerblue: 0x1e90ff,
                firebrick: 0xb22222,
                floralwhite: 0xfffaf0,
                forestgreen: 0x228b22,
                fuchsia: 0xff00ff,
                gainsboro: 0xdcdcdc,
                ghostwhite: 0xf8f8ff,
                gold: 0xffd700,
                goldenrod: 0xdaa520,
                gray: 0x808080,
                green: 0x008000,
                greenyellow: 0xadff2f,
                honeydew: 0xf0fff0,
                hotpink: 0xff69b4,
                "indianred ": 0xcd5c5c,
                indigo: 0x4b0082,
                ivory: 0xfffff0,
                khaki: 0xf0e68c,
                lavender: 0xe6e6fa,
                lavenderblush: 0xfff0f5,
                lawngreen: 0x7cfc00,
                lemonchiffon: 0xfffacd,
                lightblue: 0xadd8e6,
                lightcoral: 0xf08080,
                lightcyan: 0xe0ffff,
                lightgoldenrodyellow: 0xfafad2,
                lightgrey: 0xd3d3d3,
                lightgreen: 0x90ee90,
                lightpink: 0xffb6c1,
                lightsalmon: 0xffa07a,
                lightseagreen: 0x20b2aa,
                lightskyblue: 0x87cefa,
                lightslategray: 0x778899,
                lightsteelblue: 0xb0c4de,
                lightyellow: 0xffffe0,
                lime: 0x00ff00,
                limegreen: 0x32cd32,
                linen: 0xfaf0e6,
                magenta: 0xff00ff,
                maroon: 0x800000,
                mediumaquamarine: 0x66cdaa,
                mediumblue: 0x0000cd,
                mediumorchid: 0xba55d3,
                mediumpurple: 0x9370d8,
                mediumseagreen: 0x3cb371,
                mediumslateblue: 0x7b68ee,
                mediumspringgreen: 0x00fa9a,
                mediumturquoise: 0x48d1cc,
                mediumvioletred: 0xc71585,
                midnightblue: 0x191970,
                mintcream: 0xf5fffa,
                mistyrose: 0xffe4e1,
                moccasin: 0xffe4b5,
                navajowhite: 0xffdead,
                navy: 0x000080,
                oldlace: 0xfdf5e6,
                olive: 0x808000,
                olivedrab: 0x6b8e23,
                orange: 0xffa500,
                orangered: 0xff4500,
                orchid: 0xda70d6,
                palegoldenrod: 0xeee8aa,
                palegreen: 0x98fb98,
                paleturquoise: 0xafeeee,
                palevioletred: 0xd87093,
                papayawhip: 0xffefd5,
                peachpuff: 0xffdab9,
                peru: 0xcd853f,
                pink: 0xffc0cb,
                plum: 0xdda0dd,
                powderblue: 0xb0e0e6,
                purple: 0x800080,
                rebeccapurple: 0x663399,
                red: 0xff0000,
                rosybrown: 0xbc8f8f,
                royalblue: 0x4169e1,
                saddlebrown: 0x8b4513,
                salmon: 0xfa8072,
                sandybrown: 0xf4a460,
                seagreen: 0x2e8b57,
                seashell: 0xfff5ee,
                sienna: 0xa0522d,
                silver: 0xc0c0c0,
                skyblue: 0x87ceeb,
                slateblue: 0x6a5acd,
                slategray: 0x708090,
                snow: 0xfffafa,
                springgreen: 0x00ff7f,
                steelblue: 0x4682b4,
                tan: 0xd2b48c,
                teal: 0x008080,
                thistle: 0xd8bfd8,
                tomato: 0xff6347,
                turquoise: 0x40e0d0,
                violet: 0xee82ee,
                wheat: 0xf5deb3,
                white: 0xffffff,
                whitesmoke: 0xf5f5f5,
                yellow: 0xffff00,
                yellowgreen: 0x9acd32,
            };
            return colours[colour.toLowerCase()] || 0;
        }
    }
}
//# sourceMappingURL=GraphicsUtil.js.map